import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddLicenseDialog = ({ customer, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    creator_licenses: 0,
    viewer_licenses: 0,
    extend_months: 0,
  });

  const handleSubmit = async () => {
    if (form.creator_licenses === 0 && form.viewer_licenses === 0 && form.extend_months === 0) {
      toast.error('Please add at least one license or extend duration');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/oem/tenants/${customer.id}/add-licenses`, form);
      toast.success('Licenses added successfully!');
      onSuccess();
      setForm({ creator_licenses: 0, viewer_licenses: 0, extend_months: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add licenses');
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Licenses</DialogTitle>
          <DialogDescription>
            Add more licenses for {customer.company_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="bg-muted/50">
            <CardContent className="p-4 text-sm">
              <p className="font-medium mb-2">Current Licenses:</p>
              <p>Creators: {customer.license?.creator_licenses || 0}</p>
              <p>Viewers: {customer.license?.viewer_licenses || 0}</p>
              <p>Expires: {customer.license?.end_date ? new Date(customer.license.end_date).toLocaleDateString() : '-'}</p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Additional Creator Licenses</Label>
              <Input 
                type="number" 
                min="0" 
                value={form.creator_licenses}
                onChange={(e) => setForm(prev => ({ ...prev, creator_licenses: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Additional Viewer Licenses</Label>
              <Input 
                type="number" 
                min="0" 
                value={form.viewer_licenses}
                onChange={(e) => setForm(prev => ({ ...prev, viewer_licenses: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Extend License Duration</Label>
            <Select value={form.extend_months.toString()} onValueChange={(v) => setForm(prev => ({ ...prev, extend_months: parseInt(v) }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No Extension</SelectItem>
                <SelectItem value="1">1 Month</SelectItem>
                <SelectItem value="3">3 Months</SelectItem>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Add Licenses
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
